import { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from '@/layouts/AppLayout';
import { AdminGuard } from '@/features/admin/AdminGuard';

const Dashboard            = lazy(() => import('@/features/dashboard/DashboardPage'));

const TeilnehmerListe      = lazy(() => import('@/features/teilnehmer/TeilnehmerListePage'));
const TeilnehmerDetail     = lazy(() => import('@/features/teilnehmer/TeilnehmerDetailPage'));

const FirmenListe          = lazy(() => import('@/features/firmen/FirmenListePage'));
const FirmenDetail         = lazy(() => import('@/features/firmen/FirmenDetailPage'));

const KurseUebersicht      = lazy(() => import('@/features/kurse/KurseUebersichtPage'));
const KurseNeu             = lazy(() => import('@/features/kurse/KurseNeuPage'));
const KurseDetail          = lazy(() => import('@/features/kurse/KurseDetailPage'));
const KurseKalender        = lazy(() => import('@/features/kurse/KurseKalenderPage'));
const KurseAusbilder       = lazy(() => import('@/features/kurse/KurseAusbilderPage'));
const KurseOrte            = lazy(() => import('@/features/kurse/KurseOrtePage'));
const KurseCheckouts       = lazy(() => import('@/features/kurse/KurseCheckoutsPage'));
const KurseCheckin         = lazy(() => import('@/features/kurse/KurseCheckinPage'));

const PersonalUebersicht   = lazy(() => import('@/features/personal/PersonalUebersichtPage'));
const PersonalDetail       = lazy(() => import('@/features/personal/PersonalDetailPage'));
const PersonalGruppen      = lazy(() => import('@/features/personal/PersonalGruppenPage'));
const PersonalTeam         = lazy(() => import('@/features/personal/PersonalTeamPage'));
const PersonalVerfuegbarkeit = lazy(() => import('@/features/personal/PersonalVerfuegbarkeitPage'));

const AbrechnungRechnungen = lazy(() => import('@/features/abrechnung/RechnungenPage'));
const AbrechnungPositionen = lazy(() => import('@/features/abrechnung/PositionenPage'));
const AbrechnungExport     = lazy(() => import('@/features/abrechnung/ExportPage'));

const StatistikKurse       = lazy(() => import('@/features/statistik/StatistikKursePage'));
const StatistikAusbilder   = lazy(() => import('@/features/statistik/StatistikAusbilderPage'));
const StatistikStunden     = lazy(() => import('@/features/statistik/StatistikStundenPage'));
const StatistikKostenstellen = lazy(() => import('@/features/statistik/StatistikKostenstellenPage'));
const StatistikDebitoren   = lazy(() => import('@/features/statistik/StatistikDebitorenPage'));

const KommunikationEmail   = lazy(() => import('@/features/kommunikation/EmailPage'));
const KommunikationSms     = lazy(() => import('@/features/kommunikation/SmsPage'));
const KommunikationBenachrichtigungen = lazy(() => import('@/features/kommunikation/BenachrichtigungenPage'));

const Cloud                = lazy(() => import('@/features/cloud/CloudPage'));
const Kissy                = lazy(() => import('@/features/kissy/KissyPage'));
const Ressourcen           = lazy(() => import('@/features/ressourcen/RessourcenPage'));
const Academy              = lazy(() => import('@/features/academy/AcademyPage'));

const SupportListe         = lazy(() => import('@/features/support/SupportListePage'));
const SupportDetail        = lazy(() => import('@/features/support/SupportDetailPage'));
const SupportNew           = lazy(() => import('@/features/support/SupportNewPage'));

const Feedback             = lazy(() => import('@/features/feedback/FeedbackPage'));
const FeedbackAuswertung   = lazy(() => import('@/features/feedback/FeedbackAuswertungPage'));
const FeedbackVorschlaege  = lazy(() => import('@/features/feedback/FeedbackVorschlaegePage'));
const FeedbackMeldungen    = lazy(() => import('@/features/feedback/FeedbackMeldungenPage'));
const FeedbackVorschau     = lazy(() => import('@/features/feedback/FeedbackVorschauPage'));

const Account              = lazy(() => import('@/features/account/AccountPage'));
const Debug                = lazy(() => import('@/features/debug/DebugPage'));
const NeoDev               = lazy(() => import('@/features/neodev/NeoDevPage'));

// Admin
const AdminAllgemein       = lazy(() => import('@/features/admin/AdminAllgemeinPage'));
const AdminBenutzer        = lazy(() => import('@/features/admin/AdminBenutzerPage'));
const AdminBgQseh          = lazy(() => import('@/features/admin/AdminBgQsehPage'));
const AdminBriefoptionen   = lazy(() => import('@/features/admin/AdminBriefoptionenPage'));
const AdminBuchhaltung     = lazy(() => import('@/features/admin/AdminBuchhaltungPage'));
const AdminDsgvo           = lazy(() => import('@/features/admin/AdminDsgvoLoeschungPage'));
const AdminEmail           = lazy(() => import('@/features/admin/AdminEmailPage'));
const AdminErechnung       = lazy(() => import('@/features/admin/AdminErechnungPage'));
const AdminErinnerungen    = lazy(() => import('@/features/admin/AdminErinnerungenPage'));
const AdminErloeskonten    = lazy(() => import('@/features/admin/AdminErloeskontenPage'));
const AdminFirmenkategorien = lazy(() => import('@/features/admin/AdminFirmenkategorienPage'));
const AdminKostenstellen   = lazy(() => import('@/features/admin/AdminKostenstellenPage'));
const AdminKursarten       = lazy(() => import('@/features/admin/AdminKursartenPage'));
const AdminOnlineanmeldung = lazy(() => import('@/features/admin/AdminOnlineanmeldungPage'));
const AdminOrte            = lazy(() => import('@/features/admin/AdminOrtePage'));
const AdminPaymentDevices  = lazy(() => import('@/features/admin/AdminPaymentDevicesPage'));
const AdminPositionsvorlagen = lazy(() => import('@/features/admin/AdminPositionsvorlagenPage'));
const AdminQualifikationen = lazy(() => import('@/features/admin/AdminQualifikationenPage'));
const AdminSmsversand      = lazy(() => import('@/features/admin/AdminSmsversandPage'));
const AdminSumup           = lazy(() => import('@/features/admin/AdminSumupPage'));
const AdminTextbausteine   = lazy(() => import('@/features/admin/AdminTextbausteinePage'));
const AdminWillkommen      = lazy(() => import('@/features/admin/AdminWillkommenPage'));
const AdminZahlungsarten   = lazy(() => import('@/features/admin/AdminZahlungsartenPage'));
const AdminAutoabrechnung  = lazy(() => import('@/features/admin/AdminAutoabrechnungPage'));
const AdminBelegtransfer   = lazy(() => import('@/features/admin/AdminBelegtransferPage'));
const AdminOaStartseite    = lazy(() => import('@/features/admin/AdminOaStartseitePage'));
const AdminOaErscheinungsbild = lazy(() => import('@/features/admin/AdminOaErscheinungsbildPage'));
const AdminOaAnsprechpartner = lazy(() => import('@/features/admin/AdminOaAnsprechpartnerPage'));
const AdminOaSeo           = lazy(() => import('@/features/admin/AdminOaSeoPage'));
const AdminOaRechtlich     = lazy(() => import('@/features/admin/AdminOaRechtlichPage'));
const AdminOaUebersicht    = lazy(() => import('@/features/admin/AdminOaUebersichtPage'));

export function App() {
  return (
    <Routes>
      {/* Standalone-Seite ohne Layout (analog feedback-vorschau.php) */}
      <Route path="/feedback-vorschau/:id" element={<FeedbackVorschau />} />

      <Route element={<AppLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="dashboard" element={<Navigate to="/" replace />} />

        <Route path="teilnehmer">
          <Route index element={<TeilnehmerListe />} />
          <Route path=":id" element={<TeilnehmerDetail />} />
        </Route>

        <Route path="firmen">
          <Route index element={<FirmenListe />} />
          <Route path=":id" element={<FirmenDetail />} />
        </Route>

        <Route path="kurse">
          <Route index element={<KurseUebersicht />} />
          <Route path="neu" element={<KurseNeu />} />
          <Route path="kalender" element={<KurseKalender />} />
          <Route path="ausbilder" element={<KurseAusbilder />} />
          <Route path="orte" element={<KurseOrte />} />
          <Route path="checkouts" element={<KurseCheckouts />} />
          <Route path="checkin/:id" element={<KurseCheckin />} />
          <Route path=":id" element={<KurseDetail />} />
        </Route>

        <Route path="personal">
          <Route index element={<PersonalUebersicht />} />
          <Route path="gruppen" element={<PersonalGruppen />} />
          <Route path="team" element={<PersonalTeam />} />
          <Route path="verfuegbarkeit" element={<PersonalVerfuegbarkeit />} />
          <Route path=":id" element={<PersonalDetail />} />
        </Route>

        <Route path="abrechnung">
          <Route index element={<Navigate to="/abrechnung/rechnungen" replace />} />
          <Route path="rechnungen" element={<AbrechnungRechnungen />} />
          <Route path="positionen" element={<AbrechnungPositionen />} />
          <Route path="export"     element={<AbrechnungExport />} />
        </Route>

        <Route path="statistik">
          <Route index element={<Navigate to="/statistik/kurse" replace />} />
          <Route path="kurse"         element={<StatistikKurse />} />
          <Route path="ausbilder"     element={<StatistikAusbilder />} />
          <Route path="stunden"       element={<StatistikStunden />} />
          <Route path="kostenstellen" element={<StatistikKostenstellen />} />
          <Route path="debitoren"     element={<StatistikDebitoren />} />
        </Route>

        <Route path="kommunikation">
          <Route index element={<Navigate to="/kommunikation/email" replace />} />
          <Route path="email" element={<KommunikationEmail />} />
          <Route path="sms"   element={<KommunikationSms />} />
          <Route path="benachrichtigungen" element={<KommunikationBenachrichtigungen />} />
        </Route>

        <Route path="cloud"      element={<Cloud />} />
        <Route path="kissy"      element={<Kissy />} />
        <Route path="ressourcen" element={<Ressourcen />} />
        <Route path="academy"    element={<Academy />} />

        <Route path="support">
          <Route index element={<SupportListe />} />
          <Route path="new" element={<SupportNew />} />
          <Route path=":id" element={<SupportDetail />} />
        </Route>

        <Route path="feedback" element={<Feedback />} />
        <Route path="feedback-auswertung" element={<FeedbackAuswertung />} />
        <Route path="feedback-vorschlaege" element={<FeedbackVorschlaege />} />
        <Route path="feedback-meldungen" element={<FeedbackMeldungen />} />

        <Route path="account" element={<Account />} />
        <Route path="debug" element={<Debug />} />
        <Route path="neodev" element={<NeoDev />} />

        <Route path="admin" element={<AdminGuard />}>
          <Route index element={<Navigate to="/admin/allgemein" replace />} />
          <Route path="allgemein"          element={<AdminAllgemein />} />
          <Route path="benutzer"           element={<AdminBenutzer />} />
          <Route path="bgqseh"             element={<AdminBgQseh />} />
          <Route path="briefoptionen"      element={<AdminBriefoptionen />} />
          <Route path="buchhaltung"        element={<AdminBuchhaltung />} />
          <Route path="dsgvo-loeschung"    element={<AdminDsgvo />} />
          <Route path="emaileinstellungen" element={<AdminEmail />} />
          <Route path="erechnung"          element={<AdminErechnung />} />
          <Route path="erinnerungen"       element={<AdminErinnerungen />} />
          <Route path="erloeskonten"       element={<AdminErloeskonten />} />
          <Route path="firmenkategorien"   element={<AdminFirmenkategorien />} />
          <Route path="kostenstellen"      element={<AdminKostenstellen />} />
          <Route path="kursarten"          element={<AdminKursarten />} />
          <Route path="onlineanmeldung"    element={<AdminOnlineanmeldung />} />
          <Route path="orte"               element={<AdminOrte />} />
          <Route path="payment-devices"    element={<AdminPaymentDevices />} />
          <Route path="positionsvorlagen"  element={<AdminPositionsvorlagen />} />
          <Route path="qualifikationen"    element={<AdminQualifikationen />} />
          <Route path="smsversand"         element={<AdminSmsversand />} />
          <Route path="sumup"              element={<AdminSumup />} />
          <Route path="textbausteine"      element={<AdminTextbausteine />} />
          <Route path="willkommen"         element={<AdminWillkommen />} />
          <Route path="zahlungsarten"      element={<AdminZahlungsarten />} />
          <Route path="autoabrechnung"     element={<AdminAutoabrechnung />} />
          <Route path="belegtransfer"      element={<AdminBelegtransfer />} />
          <Route path="oa-startseite"      element={<AdminOaStartseite />} />
          <Route path="oa-erscheinungsbild"element={<AdminOaErscheinungsbild />} />
          <Route path="oa-ansprechpartner" element={<AdminOaAnsprechpartner />} />
          <Route path="oa-seo"             element={<AdminOaSeo />} />
          <Route path="oa-rechtlich"       element={<AdminOaRechtlich />} />
          <Route path="oa-uebersicht"      element={<AdminOaUebersicht />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
